import { meridiankonvergenzGrad } from './utm';

/**
 * Georeferenz eines Plans (docs/maengel-verortung-plan.md 9.2, Paket P2).
 *
 * Ein Plan wird einmal über Passpunkte an UTM gebunden: vor Ort per GPS
 * gemittelt, aus dem Absteckprotokoll abgetippt oder von einem anderen Plan
 * über gemeinsame Achskreuze übertragen. Daraus folgt eine
 * Ähnlichkeitstransformation (4 Parameter: Maßstab, Drehung, Verschiebung) —
 * und damit Nordrichtung, Maßstab und Drehung des Plans.
 *
 * Plankoordinaten wie `PlanCoordinates` in src/types.ts: Prozent 0–100 der
 * Bildbreite bzw. -höhe, y nach unten. Gerechnet wird in einem
 * rechtshändigen, maßstabstreuen System in Einheiten der Planhöhe:
 * u = x/100 · Seitenverhältnis, v = −y/100.
 *
 * Reine Funktionen, nichts wirft; Fehler der Eingabe als `{ fehler }`.
 */

/** Punkt im Plan: Prozent 0–100 der Bildbreite (x) bzw. -höhe (y, nach unten). */
export interface PlanPunkt {
    x: number;
    y: number;
}

/** Passpunkt: Stelle im Plan mit bekannter UTM-Lage (Zone der Georeferenz). */
export interface Passpunkt {
    id: string;
    name: string;
    plan: PlanPunkt;
    ost: number;
    nord: number;
    quelle: 'gps' | 'utm' | 'achse';
    genauigkeitM?: number | null;
}

/** Ergebnis der Kalibrierung eines Plans. */
export interface Georeferenz {
    zone: number;
    /** Breite/Höhe des Planbilds in Pixeln. */
    seitenverhaeltnis: number;
    /** u = x/100·seitenverhaeltnis, v = −y/100; ost = a·u − b·v + tx; nord = b·u + a·v + ty. */
    a: number;
    b: number;
    tx: number;
    ty: number;
    /** |(a,b)|·seitenverhaeltnis. */
    meterProPlanbreite: number;
    /** Gitterazimut von „Plan-oben“ = atan2(−b, a) in Grad, [0,360); 0 = genordet. */
    drehungGrad: number;
    /** RMS der Residuen ab 3 Punkten, sonst null. */
    restfehlerM: number | null;
    /** Klartext-Hinweise zur Güte, z. B. „Passpunkte nur 12 m auseinander — Drehung unsicher (± 14°)“. */
    hinweise: string[];
    punkte: Passpunkt[];
}

/** Unter diesem Abstand ist keine Georeferenz möglich. */
const MIN_ABSTAND_M = 5;
/** Unter diesem Abstand ist die Drehung unsicher (Plan 9.2: ≥ 20 m). */
const EMPFOHLENER_ABSTAND_M = 20;
/**
 * Spiegel-HINWEIS ab 3 Punkten (Codex-Review 27.09., Runde 3): passt die GESPIEGELTE Zuordnung
 * deutlich besser (Restfehler höchstens diesen Anteil) und liegt der Restfehler der richtigen über
 * dem Doppelten des quadratischen Mittels der Einzelgenauigkeiten → „vertauscht/gespiegelt?".
 * Nur Hinweis, keine Ablehnung: ein einzelner grob falscher GPS-Punkt kann das Bild vortäuschen.
 */
const SPIEGEL_ANTEIL = 0.5;
/**
 * Harter Fehler nur, wenn der ausgeglichene Maßstab praktisch null ist (unter diesem Anteil des
 * Abstandsverhältnisses Gelände/Plan) — dann ist die Georeferenz nicht sinnvoll umkehrbar.
 * GPS-Streuung allein schrumpft den Maßstab nicht um den Faktor 1000.
 */
const MIN_MASSSTAB_ANTEIL = 1e-3;
/** Angenommene Genauigkeit eines Passpunkts ohne Angabe. */
const STANDARD_GENAUIGKEIT_M = 3;

function zahlDe(wert: number, stellen: number): string {
    return wert.toFixed(stellen).replace('.', ',');
}

function meterText(m: number): string {
    return zahlDe(m, m < 10 ? 1 : 0);
}

/**
 * Winkel auf [0, 360). Rundungsreste knapp unter 360 (359,9999999998 aus
 * UTM-Werten in Millionenhöhe) werden 0 — sonst zeigt eine gerundete Anzeige
 * „360°“ statt „0°“.
 */
function gradNormalisieren(grad: number): number {
    const g = ((grad % 360) + 360) % 360;
    return g > 360 - 1e-7 ? 0 : g;
}

function planZuUv(p: PlanPunkt, seitenverhaeltnis: number): { u: number; v: number } {
    return { u: (p.x / 100) * seitenverhaeltnis, v: -p.y / 100 };
}

function punktGueltig(p: Passpunkt): boolean {
    return (
        !!p &&
        !!p.plan &&
        Number.isFinite(p.plan.x) &&
        Number.isFinite(p.plan.y) &&
        Number.isFinite(p.ost) &&
        Number.isFinite(p.nord)
    );
}

function genauigkeit(p: Passpunkt): number {
    const g = p.genauigkeitM;
    return typeof g === 'number' && Number.isFinite(g) && g > 0 ? g : STANDARD_GENAUIGKEIT_M;
}

/**
 * RMS-Restfehler der ausgeglichenen Ähnlichkeitstransformation; `gespiegelt` = mit an der
 * Plan-Senkrechten gespiegelten Planpunkten (u → −u). Nur für die Spiegel-Prüfung.
 */
function restfehlerRms(uv: readonly { u: number; v: number }[], liste: readonly Passpunkt[], gespiegelt: boolean): number {
    const n = liste.length;
    const pu = uv.map((p) => (gespiegelt ? -p.u : p.u));
    const mu = pu.reduce((s, u) => s + u, 0) / n;
    const mv = uv.reduce((s, p) => s + p.v, 0) / n;
    const me = liste.reduce((s, p) => s + p.ost, 0) / n;
    const mn = liste.reduce((s, p) => s + p.nord, 0) / n;
    let sQ = 0;
    let sA = 0;
    let sB = 0;
    for (let i = 0; i < n; i++) {
        const du = pu[i] - mu;
        const dv = uv[i].v - mv;
        const de = liste[i].ost - me;
        const dn = liste[i].nord - mn;
        sQ += du * du + dv * dv;
        sA += du * de + dv * dn;
        sB += du * dn - dv * de;
    }
    const a = sA / sQ;
    const b = sB / sQ;
    let summe = 0;
    for (let i = 0; i < n; i++) {
        const du = pu[i] - mu;
        const dv = uv[i].v - mv;
        const e = a * du - b * dv + me;
        const nn = b * du + a * dv + mn;
        summe += (e - liste[i].ost) ** 2 + (nn - liste[i].nord) ** 2;
    }
    return Math.sqrt(summe / n);
}

/**
 * Ähnlichkeitstransformation (4 Parameter) per kleinste Quadrate; ≥ 2 Punkte.
 *
 * Fehler: < 2 Punkte, ungültige Zahlen, Planpunkte identisch, Passpunkte
 * weniger als 5 m auseinander (größter Abstand zweier Punkte).
 *
 * Hinweise:
 * - größter Abstand < 20 m: „Passpunkte nur 12 m auseinander — Drehung
 *   unsicher (± 14°)“, Drehunsicherheit ≈ atan(2 · mittlere Genauigkeit /
 *   Abstand); fehlt die Genauigkeit eines Punkts, gelten 3 m.
 * - ab 3 Punkten, wenn das größte Residuum > 2 × RMS ist ODER der RMS über
 *   dem Doppelten der mittleren Genauigkeit liegt: „Restfehler 6,2 m — Punkt
 *   ‚Ecke Süd‘ prüfen (weicht 8,1 m ab)“. Die zweite Bedingung ist nötig, weil
 *   bei 3–4 Punkten das größte Residuum rechnerisch nie über 2 × RMS liegen
 *   kann (max ≤ √n · RMS).
 */
export function georeferenzBerechnen(
    punkte: readonly Passpunkt[],
    seitenverhaeltnis: number,
    zone: number,
): Georeferenz | { fehler: string } {
    if (!Number.isFinite(seitenverhaeltnis) || seitenverhaeltnis <= 0) {
        return { fehler: 'Seitenverhältnis des Plans ist ungültig — Planbild neu laden.' };
    }
    if (!Number.isInteger(zone) || zone < 1 || zone > 60) {
        return { fehler: `UTM-Zone ${zone} gibt es nicht (1–60).` };
    }
    const liste = (punkte ?? []).filter((p) => !!p);
    if (liste.length < 2) {
        return { fehler: `Mindestens 2 Passpunkte nötig — vorhanden: ${liste.length}.` };
    }
    const kaputt = liste.find((p) => !punktGueltig(p));
    if (kaputt) {
        return { fehler: `Passpunkt „${kaputt.name || kaputt.id}“ hat ungültige Koordinaten.` };
    }

    const uv = liste.map((p) => planZuUv(p.plan, seitenverhaeltnis));
    const n = liste.length;

    // Größter Abstand im Gelände und im Plan.
    let abstandM = 0;
    let abstandPlan = 0;
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            abstandM = Math.max(abstandM, Math.hypot(liste[i].ost - liste[j].ost, liste[i].nord - liste[j].nord));
            abstandPlan = Math.max(abstandPlan, Math.hypot(uv[i].u - uv[j].u, uv[i].v - uv[j].v));
        }
    }
    if (abstandPlan < 1e-9) {
        return { fehler: 'Die Passpunkte liegen im Plan an derselben Stelle — bitte verschiedene Punkte antippen.' };
    }
    if (abstandM < MIN_ABSTAND_M) {
        return {
            fehler: `Passpunkte liegen nur ${meterText(abstandM)} m auseinander — mindestens ${MIN_ABSTAND_M} m nötig, besser ${EMPFOHLENER_ABSTAND_M} m und mehr.`,
        };
    }

    // Schwerpunkte, dann Maßstab/Drehung aus den zentrierten Koordinaten.
    const mu = uv.reduce((s, p) => s + p.u, 0) / n;
    const mv = uv.reduce((s, p) => s + p.v, 0) / n;
    const me = liste.reduce((s, p) => s + p.ost, 0) / n;
    const mn = liste.reduce((s, p) => s + p.nord, 0) / n;
    let sQuadrat = 0;
    let sA = 0;
    let sB = 0;
    for (let i = 0; i < n; i++) {
        const du = uv[i].u - mu;
        const dv = uv[i].v - mv;
        const de = liste[i].ost - me;
        const dn = liste[i].nord - mn;
        sQuadrat += du * du + dv * dv;
        sA += du * de + dv * dn;
        sB += du * dn - dv * de;
    }
    const a = sA / sQuadrat;
    const b = sB / sQuadrat;
    const tx = me - a * mu + b * mv;
    const ty = mn - b * mu - a * mv;

    // Degenerierte Zuordnung (Codex-Review 27.09.): gespiegelt oder vertauscht zugeordnete Punkte
    // lassen den ausgeglichenen Maßstab schrumpfen — bei symmetrischer Lage bis a = b = 0. Ohne
    // Prüfung käme eine nicht umkehrbare Georeferenz zurück (utmZuPlan → NaN).
    const massstab = Math.hypot(a, b);
    if (!Number.isFinite(massstab) || massstab < MIN_MASSSTAB_ANTEIL * (abstandM / abstandPlan)) {
        return {
            fehler: 'Die Passpunkte passen nicht zusammen — sind Punkte im Plan vertauscht oder der Plan gespiegelt? Zuordnung prüfen.',
        };
    }

    const hinweise: string[] = [];
    const mittlereGenauigkeit = liste.reduce((s, p) => s + genauigkeit(p), 0) / n;

    if (n >= 3) {
        const rmsRichtig = restfehlerRms(uv, liste, false);
        const rmsGespiegelt = restfehlerRms(uv, liste, true);
        const genauigkeitQm = Math.sqrt(liste.reduce((s, p) => s + genauigkeit(p) ** 2, 0) / n);
        if (rmsGespiegelt <= SPIEGEL_ANTEIL * rmsRichtig && rmsRichtig > 2 * genauigkeitQm) {
            hinweise.push('Zuordnung wirkt gespiegelt — sind Punkte im Plan vertauscht? Passpunkte prüfen');
        }
    }

    if (abstandM < EMPFOHLENER_ABSTAND_M) {
        const unsicherGrad = (Math.atan((2 * mittlereGenauigkeit) / abstandM) * 180) / Math.PI;
        hinweise.push(
            `Passpunkte nur ${meterText(abstandM)} m auseinander — Drehung unsicher (± ${Math.round(unsicherGrad)}°)`,
        );
    }

    let restfehlerM: number | null = null;
    if (n >= 3) {
        const residuen = liste.map((p, i) => {
            const e = a * uv[i].u - b * uv[i].v + tx;
            const nn = b * uv[i].u + a * uv[i].v + ty;
            return Math.hypot(e - p.ost, nn - p.nord);
        });
        restfehlerM = Math.sqrt(residuen.reduce((s, r) => s + r * r, 0) / n);
        let schlimmster = 0;
        for (let i = 1; i < n; i++) if (residuen[i] > residuen[schlimmster]) schlimmster = i;
        const ausreisser = residuen[schlimmster] > 2 * restfehlerM;
        const zuGross = restfehlerM > 2 * mittlereGenauigkeit;
        if (restfehlerM > 1e-9 && (ausreisser || zuGross)) {
            const p = liste[schlimmster];
            hinweise.push(
                `Restfehler ${zahlDe(restfehlerM, 1)} m — Punkt ‚${p.name || p.id}‘ prüfen (weicht ${zahlDe(residuen[schlimmster], 1)} m ab)`,
            );
        }
    }

    return {
        zone,
        seitenverhaeltnis,
        a,
        b,
        tx,
        ty,
        meterProPlanbreite: Math.hypot(a, b) * seitenverhaeltnis,
        drehungGrad: gradNormalisieren((Math.atan2(-b, a) * 180) / Math.PI),
        restfehlerM,
        hinweise,
        punkte: liste.map((p) => ({ ...p, plan: { ...p.plan } })),
    };
}

/** Planpunkt → UTM (Zone der Georeferenz). */
export function planZuUtm(geo: Georeferenz, p: PlanPunkt): { ost: number; nord: number } {
    const { u, v } = planZuUv(p, geo.seitenverhaeltnis);
    return { ost: geo.a * u - geo.b * v + geo.tx, nord: geo.b * u + geo.a * v + geo.ty };
}

/** UTM → Planpunkt (Prozent; außerhalb des Plans < 0 bzw. > 100). */
export function utmZuPlan(geo: Georeferenz, ost: number, nord: number): PlanPunkt {
    const de = ost - geo.tx;
    const dn = nord - geo.ty;
    const det = geo.a * geo.a + geo.b * geo.b;
    const u = (geo.a * de + geo.b * dn) / det;
    const v = (-geo.b * de + geo.a * dn) / det;
    return { x: (u / geo.seitenverhaeltnis) * 100, y: -v * 100 };
}

/** Radius in Metern → Halbachsen in Plan-Prozent (x bezogen auf Breite, y auf Höhe). */
export function meterZuPlanProzent(geo: Georeferenz, meter: number): { rx: number; ry: number } {
    const planhoehen = meter / Math.hypot(geo.a, geo.b);
    return { rx: (planhoehen / geo.seitenverhaeltnis) * 100, ry: planhoehen * 100 };
}

/**
 * Liegt der Punkt auf dem Planbild? `randProzent` (Standard 0) weitet den
 * Rahmen nach außen (positiv: knapp daneben zählt noch) oder zieht ihn nach
 * innen (negativ: Punkt muss so weit vom Rand entfernt sein).
 */
export function istImPlan(p: PlanPunkt, randProzent: number = 0): boolean {
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return false;
    const rand = Number.isFinite(randProzent) ? randProzent : 0;
    return p.x >= -rand && p.x <= 100 + rand && p.y >= -rand && p.y <= 100 + rand;
}

/**
 * Magnetischer Azimut → Winkel im Plan (Grad im Uhrzeigersinn ab Plan-oben,
 * [0,360)). wahr = magnetisch + Deklination (null → 0);
 * Gitter = wahr − Meridiankonvergenz(lat, lng, geo.zone); Plan = Gitter − drehungGrad.
 */
export function kompassZuPlanwinkel(
    geo: Georeferenz,
    azimutMagnetischGrad: number,
    deklinationGrad: number | null,
    lat: number,
    lng: number,
): number {
    const deklination = typeof deklinationGrad === 'number' && Number.isFinite(deklinationGrad) ? deklinationGrad : 0;
    const wahr = azimutMagnetischGrad + deklination;
    const gitter = wahr - meridiankonvergenzGrad(lat, lng, geo.zone);
    return gradNormalisieren(gitter - geo.drehungGrad);
}

function achsNameNormalisieren(name: string): string {
    return (typeof name === 'string' ? name : '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Achsen gleichen Namens (trim, case-insensitiv): UTM über `quelle` berechnen,
 * Passpunkte mit quelle 'achse' am Zielplan liefern — in der Reihenfolge der
 * Zielachsen, je Name einmal. Genauigkeit = Restfehler der Quelle (sonst null).
 */
export function passpunkteUebertragen(
    quelle: Georeferenz,
    quellAchsen: readonly { name: string; plan: PlanPunkt }[],
    zielAchsen: readonly { name: string; plan: PlanPunkt }[],
): Passpunkt[] {
    const quellen = new Map<string, PlanPunkt>();
    for (const q of quellAchsen) {
        const schluessel = achsNameNormalisieren(q?.name);
        if (!schluessel || !q.plan || !Number.isFinite(q.plan.x) || !Number.isFinite(q.plan.y)) continue;
        if (!quellen.has(schluessel)) quellen.set(schluessel, q.plan);
    }
    const ergebnis: Passpunkt[] = [];
    const vergeben = new Set<string>();
    for (const z of zielAchsen) {
        const schluessel = achsNameNormalisieren(z?.name);
        if (!schluessel || vergeben.has(schluessel)) continue;
        if (!z.plan || !Number.isFinite(z.plan.x) || !Number.isFinite(z.plan.y)) continue;
        const qPlan = quellen.get(schluessel);
        if (!qPlan) continue;
        vergeben.add(schluessel);
        const { ost, nord } = planZuUtm(quelle, qPlan);
        ergebnis.push({
            id: `achse-${schluessel}`,
            name: z.name.trim(),
            plan: { x: z.plan.x, y: z.plan.y },
            ost,
            nord,
            quelle: 'achse',
            genauigkeitM: quelle.restfehlerM,
        });
    }
    return ergebnis;
}
